import React, {useEffect, useMemo, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import {CircularProgress} from "@mui/material";
import {toast} from "sonner";
import {Box, Checkbox, Colors, FormControlLabel, IconButton, IconClose, Text, Tooltip} from "@bounce/shared/ui";
import {useLocalStorageState} from "@bounce/shared/hooks";
import {Button, TextField} from "../../ui/components";
import {useSimulateTrigger} from "../../apis/requests/triggers/simulateTrigger";
import {
  ConditionStatus,
  ReachTriggerPayload,
  SimulateTriggerResponse,
  TriggerInteractionType,
} from "../../apis/requests/triggers/types";
import {ReachTemplateLanguage} from "../../apis/requests/templates/types";
import {useReachAuth} from "../../hooks/useReachAuth";
import RenderAsUserSection from "../templates/template-preview/RenderAsUserSection";
import {NEEDS_LANDING_PAGE_SOURCE} from "../templates/templateFormSchema";
import {LAST_TEST_EMAIL_KEY, LAST_TEST_PHONE_KEY} from "../templates/TestSendDialog";
import {PreviewButton} from "./ConditionSections";
import {PreviewTab, accountSummary, previewTabs} from "./helpers";
import {InteractionChip} from "./TriggerBadges";
import TriggerPreviewDialog from "./TriggerPreviewDialog";
import {LANGUAGE_OPTIONS} from "./triggerDisplay";
import {linkButtonSx} from "./triggerUi";
import {useAccountPicker, useRememberedAccount} from "./useAccountPicker";
import {useExamplePayload} from "./useExamplePayload";

type Props = {
  interactionType: TriggerInteractionType;
  conditionCount: number;
  conditionsKey: string;
  getTrigger: () => ReachTriggerPayload;
};

const RERUN_DELAY_MS = 400;

const STATUS_DISPLAY: Record<ConditionStatus, {label: string; color: string; background: string}> = {
  matched: {label: "Matched", color: Colors.green._800, background: Colors.green._100},
  not_matched: {label: "Didn't match", color: Colors.grey._600, background: Colors.bw.white},
  not_reached: {label: "Not reached", color: Colors.grey._600, background: Colors.bw.white},
};

const sectionSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  p: "12px 14px",
  border: `1px solid ${Colors.tertiary._300}`,
  borderRadius: "8px",
  backgroundColor: Colors.tertiary._100,
} as const;

const SectionHeader = ({title, children}: {title: string; children?: React.ReactNode}) => (
  <Box sx={{display: "flex", alignItems: "center", gap: 1.25, minHeight: 22}}>
    <Text size={12} variant="inter600" sx={{flex: 1, color: Colors.grey._800}}>
      {title}
    </Text>
    {children}
  </Box>
);

const parsePayload = (text: string): {value: Record<string, unknown> | null; error: string | null} => {
  if (!text.trim()) return {value: {}, error: null};
  try {
    const value = JSON.parse(text);
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return {value: null, error: "The payload must be a JSON object"};
    }
    return {value, error: null};
  } catch {
    return {value: null, error: "The payload is not valid JSON"};
  }
};

const payloadSummary = (interactionType: string, payload: Record<string, unknown>) =>
  [
    interactionType,
    ...Object.entries(payload)
      .filter(([, value]) => typeof value === "boolean" || typeof value === "number")
      .slice(0, 2)
      .map(([key, value]) => `${key} ${value}`),
  ].join(" · ");

const sendResultMessage = (response: SimulateTriggerResponse) => {
  const results = [...response.emails.flatMap((email) => email.results), ...(response.text?.results ?? [])];
  const failed = results.filter((result) => !result.success);
  if (!results.length) return {ok: false, text: "Nothing was sent"};
  if (failed.length) return {ok: false, text: failed.map((result) => result.error ?? "Send failed").join("; ")};
  return {ok: true, text: `Test sent (${results.length} message${results.length === 1 ? "" : "s"})`};
};

const TriggerTestPanel = ({interactionType, conditionCount, conditionsKey, getTrigger}: Props) => {
  const {agentEmail} = useReachAuth();
  const [savedEmail, setSavedEmail] = useLocalStorageState<string>(LAST_TEST_EMAIL_KEY, "", {encrypted: false});
  const [savedPhone, setSavedPhone] = useLocalStorageState<string>(LAST_TEST_PHONE_KEY, "", {encrypted: false});
  const defaultEmail = savedEmail || agentEmail;
  const [account, setAccount] = useRememberedAccount();
  const [payloadText, setPayloadText] = useState("");
  const [payloadEdited, setPayloadEdited] = useState(false);
  const [result, setResult] = useState<SimulateTriggerResponse | null>(null);
  const [ranAt, setRanAt] = useState<Date | null>(null);
  const [emails, setEmails] = useState<string[]>(defaultEmail ? [defaultEmail] : [""]);
  const [phone, setPhone] = useState(savedPhone);
  const [languages, setLanguages] = useState<ReachTemplateLanguage[]>(["en"]);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const picker = useAccountPicker(account, setAccount);

  useEffect(() => {
    if (defaultEmail) setEmails((current) => (current.some((email) => email.trim()) ? current : [defaultEmail]));
  }, [defaultEmail]);

  const {example, accountFields} = useExamplePayload(interactionType, account);
  useEffect(() => {
    if (!payloadEdited) setPayloadText(example ? JSON.stringify(example, null, 2) : "");
  }, [example, payloadEdited]);
  useEffect(() => {
    if (!accountFields) return;
    setPayloadText((current) => {
      const value = parsePayload(current).value;
      return value ? JSON.stringify({...value, ...accountFields}, null, 2) : current;
    });
  }, [accountFields]);

  const parsed = useMemo(() => parsePayload(payloadText), [payloadText]);
  const {mutateAsync: simulate, isPending: isRunning} = useSimulateTrigger();
  const {mutateAsync: testSend, isPending: isSending} = useSimulateTrigger();

  const run = async () => {
    if (!parsed.value) return;
    try {
      const response = await simulate({
        trigger: getTrigger(),
        payload: parsed.value,
        account_id: account?.account_id ?? null,
      });
      setResult(response);
      setRanAt(new Date());
    } catch {}
  };

  useEffect(() => {
    if (!result) return;
    const timer = setTimeout(run, RERUN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [conditionsKey]);

  const recipients = emails.map((email) => email.trim()).filter(Boolean);
  const phoneNumber = phone.replace(/[^\d+]/g, "");
  const sending = result?.outcome === "send" && (!!result.email_template || !!result.text_template);
  const sendBlocker = !account
    ? "Pick an account to send a test"
    : !sending
    ? "Nothing to send for this test"
    : !recipients.length && !phoneNumber
    ? "Add an email or a phone number"
    : !languages.length
    ? "Pick a language to send"
    : null;

  const toggleLanguage = (language: ReachTemplateLanguage) =>
    setLanguages((current) =>
      current.includes(language) ? current.filter((value) => value !== language) : [...current, language]
    );

  const send = async () => {
    if (sendBlocker || !account || !parsed.value) return;
    try {
      const response = await testSend({
        trigger: getTrigger(),
        payload: parsed.value,
        account_id: account.account_id,
        send_test: true,
        email_recipients: recipients,
        phone_recipient: phoneNumber || null,
        languages,
      });
      if (recipients[0] && response.emails.some((email) => email.results.some((r) => r.success))) {
        setSavedEmail(recipients[0]);
      }
      if (phone.trim() && response.text?.results.some((r) => r.success)) setSavedPhone(phone.trim());
      const message = sendResultMessage(response);
      if (message.ok) toast.success(message.text);
      else toast.error(message.text);
    } catch {}
  };

  const openPreview = (initialTab: PreviewTab) =>
    NiceModal.show(TriggerPreviewDialog, {
      interactionType,
      emailTemplate: result?.email_template ?? null,
      textTemplate: result?.text_template ?? null,
      account,
      payload: parsed.value ?? undefined,
      initialTab,
      onSendTest: sendBlocker ? undefined : send,
    });

  const sendRows = previewTabs(result?.email_template ?? null, result?.text_template ?? null)
    .filter((row) => row.tab !== "landing")
    .map((row) => ({
      ...row,
      label:
        row.tab === "text" && NEEDS_LANDING_PAGE_SOURCE.has(row.template.content_channel)
          ? `${row.label} + landing page`
          : row.label,
      detail: row.template.template_key,
    }));

  const showResult = !!result && !isRunning;

  return (
    <Box
      sx={{
        width: 400,
        flexShrink: 0,
        boxSizing: "border-box",
        p: "20px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
        backgroundColor: Colors.bw.white,
        border: `1px solid ${Colors.tertiary._300}`,
        borderRadius: "8px",
        boxShadow: "0 1px 2px 0 rgba(0,0,0,0.04)",
      }}
    >
      <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
        <Text size={18} variant="inter600" sx={{flex: 1}}>
          Test
        </Text>
        {showResult && ranAt && (
          <Text size={12} sx={{color: Colors.grey._500}}>
            Ran {ranAt.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}
          </Text>
        )}
      </Box>

      {showResult ? (
        <Box sx={sectionSx}>
          <SectionHeader title="What to test">
            <Box component="button" type="button" onClick={() => setResult(null)} sx={linkButtonSx}>
              Edit
            </Box>
          </SectionHeader>
          <Text size={13} variant="inter500">
            {account ? accountSummary(account) : "No account, payload only"}
          </Text>
          <Text
            size={12}
            sx={{
              color: Colors.grey._600,
              fontFamily: "monospace",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {payloadSummary(interactionType, parsed.value ?? {})}
          </Text>
        </Box>
      ) : (
        <Box sx={sectionSx}>
          <SectionHeader title="What to test">
            <InteractionChip interactionType={interactionType} />
          </SectionHeader>
          <RenderAsUserSection {...picker} />
          <TextField
            multiline
            minRows={6}
            maxRows={14}
            fullWidth
            value={payloadText}
            disabled={isRunning}
            onChange={(e) => {
              setPayloadText(e.target.value);
              setPayloadEdited(true);
            }}
            InputSx={{fontFamily: "monospace", fontSize: 12, backgroundColor: Colors.bw.white}}
          />
          {parsed.error && (
            <Text size={12} sx={{color: Colors.red._800}}>
              {parsed.error}
            </Text>
          )}
          <Box sx={{display: "flex", alignItems: "flex-start", gap: 1}}>
            <Text size={12} sx={{flex: 1, color: Colors.grey._600}}>
              {account
                ? "Fields about the consumer come from this account. Change any value to try a case."
                : "An example with every rule field. Pick an account to fill in its real values."}{" "}
              Money is in cents (12500 = $125.00).
            </Text>
            {payloadEdited && (
              <Box
                component="button"
                type="button"
                onClick={() => setPayloadEdited(false)}
                sx={{...linkButtonSx, color: Colors.grey._600}}
              >
                Reset
              </Box>
            )}
          </Box>
        </Box>
      )}

      {isRunning && (
        <Box sx={sectionSx}>
          <SectionHeader title="What matched" />
          {Array.from({length: Math.max(conditionCount, 1)}, (_, i) => (
            <Box key={i} sx={{height: 31, borderRadius: "5px", backgroundColor: Colors.tertiary._200}} />
          ))}
        </Box>
      )}

      {showResult && result && (
        <>
          <Box sx={sectionSx}>
            <SectionHeader title="What matched" />
            <Box
              component="ol"
              sx={{m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 0.75}}
            >
              {result.results.map((condition, index) => {
                const display = STATUS_DISPLAY[condition.status];
                return (
                  <Box
                    component="li"
                    key={`${condition.name}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      p: "8px 11px",
                      borderRadius: "6px",
                      backgroundColor: display.background,
                    }}
                  >
                    <Text size={13} sx={{width: 14, color: Colors.grey._600}}>
                      {index + 1}
                    </Text>
                    <Text
                      size={13}
                      variant={condition.status === "matched" ? "inter500" : undefined}
                      sx={{flex: 1, color: condition.status === "matched" ? Colors.grey._900 : Colors.grey._600}}
                    >
                      {condition.name}
                    </Text>
                    <Text size={13} variant="inter600" sx={{color: display.color}}>
                      {display.label}
                    </Text>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <SectionHeader title="What to send" />
            {!result.matched_condition ? (
              <Text size={13} sx={{color: Colors.grey._700}}>
                No condition matched.
              </Text>
            ) : result.outcome === "do_not_send" ? (
              <Text size={13} sx={{color: Colors.grey._700}}>
                &quot;{result.matched_condition}&quot; matched: Don&apos;t send.
              </Text>
            ) : (
              sendRows.map((row) => (
                <Box
                  key={row.tab}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    p: "9px 11px",
                    border: `1px solid ${Colors.tertiary._300}`,
                    borderRadius: "8px",
                    backgroundColor: Colors.bw.white,
                  }}
                >
                  <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column"}}>
                    <Text size={13} variant="inter500">
                      {row.label}
                    </Text>
                    <Text
                      size={12}
                      sx={{
                        color: Colors.grey._600,
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.detail}
                    </Text>
                  </Box>
                  <PreviewButton disabled={false} onClick={() => openPreview(row.tab)} />
                </Box>
              ))
            )}
          </Box>

          <Box sx={sectionSx}>
            <SectionHeader title="Where to send">
              <Box
                component="button"
                type="button"
                onClick={() => setRecipientsOpen((open) => !open)}
                sx={linkButtonSx}
              >
                {recipientsOpen ? "Done" : "Change"}
              </Box>
            </SectionHeader>
            {recipientsOpen ? (
              <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                {emails.map((email, index) => (
                  <Box key={index} sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <TextField
                      size="small"
                      fullWidth
                      type="email"
                      placeholder="name@finbounce.com"
                      value={email}
                      onChange={(e) => setEmails(emails.map((value, i) => (i === index ? e.target.value : value)))}
                      InputSx={{fontFamily: "monospace", fontSize: 12, backgroundColor: Colors.bw.white}}
                    />
                    {index === 0 && agentEmail && email === agentEmail ? (
                      <Text size={11} sx={{color: Colors.grey._500}}>
                        You
                      </Text>
                    ) : (
                      index > 0 && (
                        <IconButton
                          size="small"
                          aria-label="Remove email"
                          onClick={() => setEmails(emails.filter((_, i) => i !== index))}
                        >
                          <IconClose width={14} height={14} />
                        </IconButton>
                      )
                    )}
                  </Box>
                ))}
                <Box
                  component="button"
                  type="button"
                  onClick={() => setEmails([...emails, ""])}
                  sx={{...linkButtonSx, alignSelf: "flex-start"}}
                >
                  + Add email
                </Box>
                <Text size={12} sx={{color: Colors.grey._600}}>
                  Phone, for SMS and MMS
                </Text>
                <TextField
                  size="small"
                  fullWidth
                  type="tel"
                  placeholder="+1 555 010 0000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  InputSx={{fontFamily: "monospace", fontSize: 12, backgroundColor: Colors.bw.white}}
                />
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                  <Text size={12} sx={{color: Colors.grey._600}}>
                    Languages
                  </Text>
                  {LANGUAGE_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      sx={{m: 0}}
                      control={
                        <Checkbox
                          checked={languages.includes(option.value)}
                          onChange={() => toggleLanguage(option.value)}
                          sx={{p: 0, mr: 0.75}}
                        />
                      }
                      label={<Text size={12}>{option.label}</Text>}
                    />
                  ))}
                </Box>
              </Box>
            ) : (
              <Text
                size={12}
                sx={{fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
              >
                {[recipients.join(", ") || "No email", phone.trim() || "no phone"].join(" · ")}
              </Text>
            )}
          </Box>
        </>
      )}

      {showResult ? (
        <Tooltip title={sendBlocker ?? ""}>
          <span>
            <Button
              buttonType="contained-primary"
              fullWidth
              onClick={send}
              disabled={!!sendBlocker || isSending}
              sx={{height: 40}}
            >
              {isSending ? <CircularProgress size={18} sx={{color: Colors.bw.white}} /> : "Send test"}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          buttonType="contained-primary"
          fullWidth
          onClick={run}
          disabled={isRunning || !parsed.value}
          sx={{height: 40}}
        >
          {isRunning ? `Evaluating ${conditionCount} conditions…` : "Run simulation"}
        </Button>
      )}
    </Box>
  );
};

export default TriggerTestPanel;
